import { Box, Flex, Link } from '@joinrepublic/dls-ui-kit';
import { colors } from '@joinrepublic/republic-ui-levelup-theme';

import type { MaybeValue, Viewer } from '@r-client/shared/data/auth';

import { LEVELUP_HOME_URL } from '../../utils/levelup-urls';
import { Logo } from '../logo';
import { LevelupUserMenu } from '../user-menu/user-menu';

export interface ILevelupMainHeaderProps {
  overlay?: boolean;
  sticky?: boolean;
  user: {
    viewer: MaybeValue<Viewer>;
    isLoading?: boolean;
  };
  /**
   * Where the branded auth pages live for this request's host: `/levelup` on the main host, and
   * the empty string on LevelUp's own, where they sit at the root. Resolved server-side and
   * passed down -- reading `window` here would differ between SSR and hydration.
   */
  authPathPrefix?: string;
}

// Figma's "Investment / Success / WIP" frame (node `1512:5604`) insets the header 31px from the
// top of the page. Padding rather than margin, so the header's own dark background fills the gap
// instead of exposing whatever sits above it.
const TOP_INSET = '31px';

// Matches the `Container` maxWidth of the invest checkout below it, so the logo and the menu line
// up with the page content.
const CONTENT_WIDTH = '1400px';

const overlayStyles = {
  position: 'absolute',
  top: '0',
  right: '0',
  left: '0',
} as const;

const stickyStyles = { position: 'sticky', top: '0' } as const;

/**
 * LevelUp's own header: the wordmark and the account menu, and nothing else.
 *
 * A separate component rather than a theme of the Republic header. It shares no navigation, no
 * search, no notifications and no user menu with it — only the outer box — so expressing it as
 * `theme === 'levelup'` branches inside `DesktopHeader` and `MobileHeader` meant six conditions
 * that had to agree, and the mobile one had already drifted: it swapped the logo and left the
 * Republic navigation on a black bar.
 *
 * One component for both widths: the design differs only in the size of the mark.
 */
export function LevelupMainHeader({
  overlay = false,
  sticky = false,
  user,
  authPathPrefix,
}: ILevelupMainHeaderProps) {
  return (
    <Box
      // Taken from the theme rather than the semantic token: this header renders above the page
      // content, outside `NestedLevelupThemeProvider`, and mounting a second provider here would
      // put two elements with the same `id` on the invest page, which already mounts one.
      bgColor={colors.grey['950']}
      position="relative"
      zIndex="modal"
      pt={{ base: undefined, md: TOP_INSET }}
      {...(overlay && overlayStyles)}
      {...(sticky && stickyStyles)}
    >
      {/* `lg: '0'` matches the invest Container's zeroed desktop gutter, so the logo sits on the
          content's left edge where the two-column layout begins (`columnBreakpoint: lg`). */}
      <Box maxW={CONTENT_WIDTH} m="auto" px={{ base: '6', md: '4', lg: '0' }}>
        <Flex alignItems="center" py={{ base: '3', md: '0' }}>
          <Box mr="2">
            {/* Default 123x53 matches Figma's "Logo / Full" (node `1408:2`); the header's height
                is driven entirely by the mark, with no padding of its own. The logo links to
                LevelUp's own homepage. */}
            <Link
              href={LEVELUP_HOME_URL}
              display="inline-flex"
              alignItems="center"
            >
              <Box display={{ base: 'none', md: 'block' }}>
                <Logo />
              </Box>
              <Box display={{ base: 'block', md: 'none' }}>
                <Logo width={77} height={34} />
              </Box>
            </Link>
          </Box>

          <Box ml="auto">
            <LevelupUserMenu user={user} authPathPrefix={authPathPrefix} />
          </Box>
        </Flex>
      </Box>
    </Box>
  );
}
